import { api } from "./api";
import {
  ListRequest,
  ListRequestSchemaV2,
  MessageResponse,
  PaginatedResponse,
} from "../types/common/commonTypes";
import {
  SalesOrderCreate,
  SalesOrderFull,
  SalesOrderMin,
  SalesOrderListView,
  SalesOrderUpdate,
  PaginatedSOResponse,
  SOFulfillment,
  SalesOrderStatusUpdate,
} from "../types/salesOrderTypes";
import {
  OrderStatusUpdate,
  CompanyOrderListView,
} from "../types/combinedTypes";

/**
 * Inventory Management Service
 *
 * Provides complete integration with all 12 inventory management API endpoints:
 * - Core lot management (CRUD operations)
 * - FIFO allocation engine
 * - Real-time availability tracking
 * - Lot shipping and movement
 * - Comprehensive inventory summaries
 *
 * Auto-generation: Lot numbers are auto-generated by backend (YY-###### format)
 * Example: 24-000001, 24-000002, etc. (resets annually)
 */
const so_base = "/orders/sales";
export const salesOrderService = {
  create: async (data: SalesOrderCreate): Promise<SalesOrderFull> => {
    const response = await api.post(`${so_base}/`, data);
    return response.data;
  },
  list: async (params: ListRequest): Promise<PaginatedSOResponse> => {
    const response = await api.post(`${so_base}/search`, params);
    return response.data;
  },
  listmin: async (): Promise<SalesOrderMin[]> => {
    const response = await api.get(`${so_base}/min`);
    return response.data;
  },
  listByCustomer: async (
    params: ListRequestSchemaV2,
  ): Promise<PaginatedResponse<CompanyOrderListView>> => {
    const response = await api.post(`${so_base}/by_customer`, params);
    return response.data;
  },

  /**
   * Get a specific inventory lot by ID
   * GET /api/v1/inventory/lots/{lot_id}
   */
  getById: async (id: string): Promise<SalesOrderFull> => {
    const response = await api.get(`${so_base}/${id}`);
    return response.data;
  },
  postProcess: async (id: string): Promise<MessageResponse> => {
    const response = await api.put(`${so_base}/postprocess/${id}`);
    return response.data;
  },
  regenDoc: async (id: string): Promise<MessageResponse> => {
    const response = await api.put(`${so_base}/regendocs/${id}`);
    return response.data;
  },
  checkDupePO: async (data: {
    cust_po: string;
    exclude_so: string[];
  }): Promise<MessageResponse> => {
    const response = await api.post(`${so_base}/cust-po`, data);
    return response.data;
  },

  /**
   * Update an inventory lot
   * PUT /api/v1/inventory/lots/{lot_id}
   */
  update: async (
    id: string,
    data: SalesOrderUpdate,
  ): Promise<SalesOrderFull> => {
    const response = await api.put(`${so_base}/${id}`, { ...data, id });
    return response.data;
  },
  updateApproval: async (
    id: string,
    data: SalesOrderStatusUpdate,
  ): Promise<SalesOrderFull> => {
    const response = await api.put(`${so_base}/approve/${id}`, { ...data, id });
    return response.data;
  },
  markComplete: async (id: string): Promise<SalesOrderFull> => {
    const response = await api.put(`${so_base}/complete/${id}`, { id });
    return response.data;
  },
  markVoid: async (id: string): Promise<SalesOrderFull> => {
    const response = await api.put(`${so_base}/void/${id}`, { id });
    return response.data;
  },
  statusUpdate: async (data: OrderStatusUpdate): Promise<MessageResponse> => {
    const response = await api.post(`${so_base}/status-update`, data);
    return response.data;
  },

  /**
   * Update an inventory lot
   * PUT /api/v1/inventory/lots/{lot_id}
   */
  fulfill: async (id: string, data: SOFulfillment): Promise<SalesOrderFull> => {
    const response = await api.post(`${so_base}/fulfill/${id}`, {
      ...data,
      id,
    });
    return response.data;
  },

  /**
   * Delete an inventory lot (soft delete)
   * DELETE /api/v1/inventory/lots/{lot_id}
   */
  deactivate: async (id: string): Promise<MessageResponse> => {
    const response = await api.delete(`${so_base}/${id}`);
    return response.data;
  },

  /**
   * Restore a deleted inventory lot
   * POST /api/v1/inventory/lots/{lot_id}/restore
   */
  reactivate: async (id: string): Promise<SalesOrderFull> => {
    const response = await api.post(`${so_base}/restore/${id}`);
    return response.data;
  },
};
